import { IWcBindable, WcsAccelerometerErrorDetail } from "../types.js";
import { AccelerometerCore } from "../core/AccelerometerCore.js";
import { WcsIoErrorInfo } from "../core/platformCapability.js";
import { upgradeProperties } from "../protocol/upgradeProperties.js";
import { reflectAttribute } from "../protocol/inputAttribute.js";

/**
 * `<wcs-accelerometer>` — declarative Generic Sensor API (`Accelerometer`)
 * monitor + start/stop control.
 *
 * Unlike `<wcs-network>` / `<wcs-permission>` (pure monitors), this Shell is a
 * bidirectional node: `start`/`stop` commands (command-token: state → element)
 * alongside the `x`/`y`/`z`/`error` observable surface (event-token: element →
 * state). The `frequency` attribute is the sole configuration input, forwarded
 * to the platform `Accelerometer` constructor's `{ frequency }` option
 * (docs/sensor-tag-design.md §1.2). The getter normalizes it: a non-finite or
 * non-positive value (NaN, 0, negative) reads back as `null` — meaning "no
 * frequency specified" — so start() falls back to the platform default rather
 * than forwarding a value the sensor would reject. Any positive finite value is
 * passed through verbatim (no upper-bound clamping — an out-of-range-but-positive
 * rate is still left to the browser/sensor to reject via `error`).
 *
 * Permission handling is intentionally NOT implemented here. Compose with
 * `<wcs-permission name="accelerometer">` instead (see the README's permission
 * example, "Gate on permission, then start", and docs/sensor-tag-design.md).
 */
export class WcsAccelerometer extends HTMLElement {
  static hasConnectedCallbackPromise = true;

  static wcBindable: IWcBindable = {
    ...AccelerometerCore.wcBindable,
    inputs: [{ name: "frequency" }],
    // Core の commands をそのまま継承（単一情報源）。
    commands: AccelerometerCore.wcBindable.commands,
  };

  private _core: AccelerometerCore;
  private _connectedCallbackPromise: Promise<void> = Promise.resolve();
  private _internals: ElementInternals | null = null;

  constructor() {
    super();
    this._core = new AccelerometerCore(this);
    this._internals = this._initInternals();
    this._wireStates({
      "wcs-accelerometer:error": (d) => ({ error: d != null }),
    });
  }

  // CSS state reflection (:state()) — debug-only snapshot getter. NOT part of
  // wc-bindable (not a bind target); see README "CSS styling with :state()".
  // MUST NOT return the live CustomStateSet (that would let callers write
  // states from outside, defeating the point of :state() being read-only).
  get debugStates(): string[] {
    return this._internals ? [...this._internals.states] : [];
  }

  private _initInternals(): ElementInternals | null {
    // never-throw (docs/async-io-node-guidelines.md §3.6): attachInternals is
    // absent in happy-dom / older environments, and pre-125 Chromium rejects
    // non-dashed state names from states.add() (probed and discarded here).
    // Either case silently disables reflection — the component still works,
    // it just doesn't expose :state() selectors.
    try {
      if (typeof this.attachInternals !== "function") return null;
      const internals = this.attachInternals();
      internals.states.add("wcs-probe");
      internals.states.delete("wcs-probe");
      return internals;
    } catch {
      return null;
    }
  }

  private _wireStates(map: Record<string, (detail: any) => Record<string, boolean>>): void {
    if (this._internals === null) return;
    const states = this._internals.states;
    for (const [event, toStates] of Object.entries(map)) {
      this.addEventListener(event, (e) => {
        const debug = this.hasAttribute("debug-states");
        for (const [name, on] of Object.entries(toStates((e as CustomEvent).detail))) {
          try {
            if (on) { states.add(name); } else { states.delete(name); }
          } catch { /* never-throw */ }
          if (debug) this.toggleAttribute(`data-wcs-state-${name}`, on);
        }
      });
    }
  }

  // --- Attribute accessors ---

  /**
   * Sampling frequency in Hz. Reads back `null` when unset, blank, or when the
   * attribute does not parse to a positive finite number (NaN, `"0"`, negative)
   * — in every such "no usable value" case the platform default applies.
   *
   * Note the deliberate set/get asymmetry: `set frequency(0)` (or any
   * non-positive/non-finite value) still writes the attribute verbatim for
   * transparency/inspectability, but the getter normalizes it back to `null`.
   * A round-trip through a non-positive value therefore does NOT preserve it —
   * that value carries no valid sampling meaning, so it is treated as "unset"
   * on read. Only positive finite frequencies survive a set→get round-trip.
   */
  get frequency(): number | null {
    const attr = this.getAttribute("frequency");
    if (attr === null || attr.trim() === "") return null;
    const parsed = Number(attr);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  set frequency(value: number | null | undefined) {
    // `null` removes the attribute (the platform default); `undefined` restores
    // the attribute the element started with — the authored frequency, or none
    // (wc-bindable producer guidance P1; React 19 and a direct assignment
    // deliver it, @wcstack/state does not).
    reflectAttribute(this, "frequency", value);
  }

  // --- Core delegated getters ---

  get x(): number | null {
    return this._core.x;
  }

  get y(): number | null {
    return this._core.y;
  }

  get z(): number | null {
    return this._core.z;
  }

  get error(): WcsAccelerometerErrorDetail | null {
    return this._core.error;
  }

  get errorInfo(): WcsIoErrorInfo | null {
    return this._core.errorInfo;
  }

  get connectedCallbackPromise(): Promise<void> {
    return this._connectedCallbackPromise;
  }

  // --- Commands ---

  start(): void {
    this._core.start(this.frequency ?? undefined);
  }

  stop(): void {
    this._core.stop();
  }

  // --- Lifecycle ---

  // Deliberately does NOT auto-start the sensor on connect. Unlike
  // Geolocation (whose default phase acquires a fix immediately unless
  // `manual` is set), Accelerometer has no such "connect implies observing"
  // precedent in the design doc (docs/sensor-tag-design.md §1.3):
  // start/stop are the only commands, so connecting the element merely makes
  // it inert until a command-token `start` (or the `start()` method) is
  // invoked. This also keeps behavior predictable when composed with
  // `<wcs-permission name="accelerometer">`: the caller decides when to start,
  // typically gated on `granted`.
  connectedCallback(): void {
    // upgrade 前に代入された input を取り込み直す（doc 13 §1.2 / Phase A1）
    upgradeProperties(this);
    this.style.display = "none";
    // No asynchronous probe to await (docs/async-io-node-guidelines.md §3.8);
    // kept for SSR uniformity with other IO nodes.
    this._connectedCallbackPromise = this._core.ready;
  }

  disconnectedCallback(): void {
    this._core.dispose();
  }
}
